import React, { useId, useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ProductMappingsManager } from '@/components/admin/ProductMappingsManager';
import { ProductIdentifierSelector } from '@/components/admin/ProductIdentifierSelector';
import { featureFlags } from '@/utils/featureFlags';
import { getMerchantTypeLabel, MerchantType } from '@/components/admin/MerchantTypeSelector';
import { deriveMerchantStatus, getMerchantStatus } from '@/shared/merchantStatus';
import { MerchantRecord } from '@/shared/types/merchant';
import {
  AmazonFinanceSourceSettings,
  organisationHasAmazonSellingPartner,
} from './AmazonFinanceSourceSettings';
import { CorporateStructureSection } from './CorporateStructureSection';
import { CustomCsvIdentifierSection } from './CustomCsvIdentifierSection';
import { DataFeedsSection } from './DataFeedsSection';
import { LicensedSellerMonitoringSection } from './LicensedSellerMonitoringSection';
import { useMerchantConnections } from '@/hooks/useMerchantConnections';
import { ExpectedSalesCostsSection } from './ExpectedSalesCostsSection';
import { InventoryValueChainSection } from './InventoryValueChainSection';
import { VariantMappingSummary } from './VariantMappingSummary';
import type { ConnectionsMap } from '@/shared/merchantFeeds';
import { MerchantClassificationSection } from './MerchantClassificationSection';
import { MerchantContactsSection } from './MerchantContactsSection';
import { MerchantDetailsSection } from './MerchantDetailsSection';
import { ReportsDataSourcesSection } from './ReportsDataSourcesSection';
import { SettingsGroup, SettingsSection } from './SettingsGroup';

// Merchant › Settings tab (FIG-320 / REQ-022 revision of 16 Oct 2026):
// collapsible groups, collapsed by default, with Expand all / Collapse all.
// Later sub-PRs add Data Feeds (FIG-321), Valuation (FIG-322) and Licensed
// Seller Monitoring (FIG-323).
//
// REQ-023 / REQ-030 remove Custom CSV Identifier and Product Identifier as
// "covered by Variant Mapping", but Variant Mapping does not manage either yet
// and both still drive CSV parsing and reports. They are kept, grouped as
// identifier settings, until that is decided (WORK_PLAN FIG-320, Q10).

//
// Admin mode (FIG-345 item 7): admins see an "Admin mode" switch. While it is
// off the tab is read-only (no pen icons, actions, include/exclude or remove
// controls); while it is on an "Editing" banner shows. Turning it off drops
// any open, unsaved edit. The page owns the on/off state (useAdminMode) and
// passes the result in as `canEdit`; Delete merchant stays with `isAdmin`.

export type SettingsGroupId = 'merchant' | 'data' | 'valuation' | 'reports' | 'monitoring';

export interface AdminModeControl {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  /** True while admin mode can't be turned on yet (e.g. the user's email isn't loaded). */
  disabled?: boolean;
}

interface MerchantSettingsTabProps {
  merchant: MerchantRecord;
  connections: unknown[];
  /** Whether edit controls show: an admin with admin mode on (or an admin, when `adminMode` is omitted). */
  canEdit: boolean;
  /** Whether the viewer is a Figabl admin. Defaults to `canEdit`. */
  isAdmin?: boolean;
  /** Renders the Admin mode switch for admins. */
  adminMode?: AdminModeControl;
  onUpdated: () => void;
  /** Opens Inventory › Variant Mapping (from the Variant Mapping summary). */
  onOpenVariantMapping?: () => void;
  /** Opens the Inventory tab (e.g. to upload a CSV snapshot). */
  onOpenInventory?: () => void;
  /** Rendered beside Expand all / Collapse all, e.g. the merchant actions. */
  actions?: React.ReactNode;
  /** Rendered after the groups, e.g. the Delete merchant danger zone. */
  footer?: React.ReactNode;
}

const ALL_GROUPS: SettingsGroupId[] = ['merchant', 'data', 'valuation', 'reports', 'monitoring'];

interface ConnectionLike {
  id?: string;
  service?: string;
  import_type?: string;
  ecommerce_subtype?: string;
}

// Same rule the Settings tab used before FIG-320: Product Identifier applies
// when the merchant has Amazon SP-API, Shopify or an Amazon CSV import.
function hasProductIdentifierSources(connections: unknown): boolean {
  if (!connections || typeof connections !== 'object') return false;
  const list = (Array.isArray(connections) ? connections : Object.values(connections)) as ConnectionLike[];
  const fivetran = list.filter((c) => c?.service && c.service !== 'file' && !c.id?.startsWith('csv-import-'));
  return fivetran.some((c) => c.service === 'amazon_selling_partner' || c.service === 'shopify')
    || list.some((c) => c?.import_type === 'ecommerce' && c.ecommerce_subtype === 'amazon');
}

export const MerchantSettingsTab: React.FC<MerchantSettingsTabProps> = ({ merchant, connections, canEdit, isAdmin, adminMode, onUpdated, onOpenVariantMapping, onOpenInventory, actions, footer }) => {
  const viewerIsAdmin = isAdmin ?? canEdit;
  const adminModeSwitchId = useId();
  const [open, setOpen] = useState<Record<SettingsGroupId, boolean>>({ merchant: false, data: false, valuation: false, reports: false, monitoring: false });
  const toggle = (id: SettingsGroupId) => () => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const setAll = (value: boolean) =>
    setOpen(Object.fromEntries(ALL_GROUPS.map((id) => [id, value])) as Record<SettingsGroupId, boolean>);

  const typeLabel = getMerchantTypeLabel((merchant.merchant_type || 'direct') as MerchantType);
  const statusLabel = getMerchantStatus(deriveMerchantStatus(merchant)).label;
  const showAmazonFinance = organisationHasAmazonSellingPartner(merchant.connections);
  // Stable owner so the connection status polling only re-runs when connections change.
  const connectionsOwner = useMemo(() => ({
    id: merchant.id,
    connections: merchant.connections as ConnectionsMap | null | undefined,
    is_test_clone: merchant.is_test_clone,
    source_organisation_id: merchant.source_organisation_id,
  }), [merchant.id, merchant.connections, merchant.is_test_clone, merchant.source_organisation_id]);
  // One instance shared by Data Feeds and Licensed Seller Monitoring, so each
  // connection's live status is fetched once.
  // Status is only fetched once a group listing connections is opened.
  const connectionsApi = useMerchantConnections(connectionsOwner, onUpdated, { enabled: open.data || open.monitoring });
  const showProductIdentifier = hasProductIdentifierSources(merchant.connections);

  return (
    <div className="space-y-4" data-testid="merchant-settings-tab">
      {!viewerIsAdmin && (
        <div role="note" className="rounded-md border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          You're viewing these settings. Only Figabl admins can make changes.
        </div>
      )}
      {viewerIsAdmin && adminMode?.enabled && (
        <div
          role="status"
          data-testid="admin-mode-banner"
          className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <Pencil className="h-4 w-4 flex-none" aria-hidden="true" />
          <span><span className="font-semibold">Editing.</span> Changes you save here apply to this merchant. Turn off Admin mode when you're done.</span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>{canEdit ? actions : null}</div>
        <div className="flex flex-wrap items-center gap-4">
          {viewerIsAdmin && adminMode && (
            <div className="flex items-center gap-2">
              <Switch
                id={adminModeSwitchId}
                checked={adminMode.enabled}
                onCheckedChange={adminMode.onChange}
                // Only blocks turning it on; an enabled switch can always be turned off.
                disabled={!!adminMode.disabled && !adminMode.enabled}
                aria-describedby={`${adminModeSwitchId}-hint`}
              />
              <Label htmlFor={adminModeSwitchId} className="text-sm font-medium">Admin mode</Label>
              <span id={`${adminModeSwitchId}-hint`} className="sr-only">
                {adminMode.enabled
                  ? 'Settings can be edited.'
                  : adminMode.disabled
                    ? 'Available once your account has loaded.'
                    : 'Turn on to make changes in Settings.'}
              </span>
            </div>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setAll(true)}>Expand all</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setAll(false)}>Collapse all</Button>
          </div>
        </div>
      </div>
      {viewerIsAdmin && adminMode && !adminMode.enabled && (
        <p className="text-sm text-muted-foreground" data-testid="admin-mode-hint">
          Settings are read-only. Turn on Admin mode to make changes.
        </p>
      )}

      {/* Keyed on canEdit so turning admin mode off remounts the sections and
          drops any open, unsaved edit. Group open/closed state lives above. */}
      <React.Fragment key={canEdit ? 'edit' : 'view'}>
      <SettingsGroup
        id="merchant"
        title="Merchant"
        summary={`Details, classification, corporate structure and contacts · ${typeLabel} · ${statusLabel}`}
        open={open.merchant}
        onToggle={toggle('merchant')}
      >
        <MerchantDetailsSection merchant={merchant} canEdit={canEdit} onUpdated={onUpdated} />
        <MerchantClassificationSection merchant={merchant} canEdit={canEdit} onUpdated={onUpdated} />
        <CorporateStructureSection canEdit={canEdit} />
        <MerchantContactsSection merchant={merchant} canEdit={canEdit} canView={viewerIsAdmin} onUpdated={onUpdated} />
      </SettingsGroup>

      <SettingsGroup
        id="data"
        title="Pricing Data In"
        summary="Bank Feeds, Inventory Feeds, Sales Transaction Feeds, Seller Central API Data Settings and Variant Mapping"
        open={open.data}
        onToggle={toggle('data')}
      >
        <DataFeedsSection
          merchant={connectionsOwner}
          connections={connectionsApi}
          canEdit={canEdit}
        />
        <SettingsSection
          title="Seller Central API Data Settings"
          description="How this merchant's Amazon data is read."
        >
          {showAmazonFinance ? (
            <AmazonFinanceSourceSettings
              organisationId={merchant.id}
              currentValue={merchant.amazon_finance_source}
              onUpdate={onUpdated}
              disabled={!canEdit}
            />
          ) : (
            <p className="text-sm text-muted-foreground">No Amazon Seller Central API connection for this merchant.</p>
          )}
        </SettingsSection>
        <SettingsSection
          title="Identifier settings"
          description="Planned for removal once Variant Mapping manages identifiers (REQ-023, REQ-030). Kept for now because CSV uploads and reports still use them."
        >
          <div className="space-y-8">
            {/* These legacy controls take no canEdit prop; a disabled fieldset
                makes their native inputs and buttons read-only. */}
            <fieldset disabled={!canEdit} className="m-0 min-w-0 border-0 p-0" data-testid="custom-csv-identifier-fieldset">
              <CustomCsvIdentifierSection organisationId={merchant.id} />
            </fieldset>
            {showProductIdentifier && (
              <ProductIdentifierSelector
                organisationId={merchant.id}
                currentValue={merchant.product_identifier || 'SKU'}
                onUpdate={onUpdated}
                disabled={!canEdit}
              />
            )}
          </div>
        </SettingsSection>
        <VariantMappingSummary
          organisationId={merchant.id}
          // Inventory shows Variant Mapping to admins only, behind its feature flag.
          // Opening it is navigation, not an edit, so it follows isAdmin, not admin mode.
          onOpenVariantMapping={viewerIsAdmin && featureFlags.showVariantMapping ? onOpenVariantMapping : undefined}
        />
        {featureFlags.showProductMappings && (
          <SettingsSection title="Product Mappings">
            <fieldset disabled={!canEdit} className="m-0 min-w-0 border-0 p-0">
              <ProductMappingsManager organisationId={merchant.id} productIdentifier={merchant.product_identifier ?? 'SKU'} />
            </fieldset>
          </SettingsSection>
        )}
      </SettingsGroup>

      <SettingsGroup
        id="valuation"
        title="Valuation"
        summary="Inventory Value Chain and Expected Sales Costs"
        open={open.valuation}
        onToggle={toggle('valuation')}
      >
        <InventoryValueChainSection
          merchantId={merchant.id}
          merchantName={merchant.name}
          isReseller={merchant.merchant_type === 'reseller'}
          canEdit={canEdit}
          connections={merchant.connections as ConnectionsMap | null | undefined}
          onOpenInventory={onOpenInventory}
        />
        <ExpectedSalesCostsSection
          organisationId={merchant.id}
          value={merchant.dr_sku_percentage}
          canEdit={canEdit}
          onUpdated={onUpdated}
        />
      </SettingsGroup>

      <SettingsGroup
        id="reports"
        title="Reports and Charts"
        summary="Data sources for ∑: Variants, Sales History, Fixed Costs and the Daily NERV Trends chart"
        open={open.reports}
        onToggle={toggle('reports')}
      >
        <ReportsDataSourcesSection
          organisationId={merchant.id}
          connections={connections}
          merchantConnections={merchant.connections}
          reportDataSources={merchant.report_data_sources}
          costAllocationConfig={merchant.cost_allocation_config}
          canEdit={canEdit}
          onUpdated={onUpdated}
        />
      </SettingsGroup>

      <SettingsGroup
        id="monitoring"
        title="Licensed Seller Monitoring"
        summary="Confirms the merchant stays solvent and in good standing: its own bank accounts (Plaid) and accounting software (Fivetran)"
        open={open.monitoring}
        onToggle={toggle('monitoring')}
      >
        <LicensedSellerMonitoringSection merchant={connectionsOwner} connections={connectionsApi} canEdit={canEdit} />
      </SettingsGroup>
      </React.Fragment>

      {footer}
    </div>
  );
};

export default MerchantSettingsTab;
